import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { dictionary } from "./dictionary";

export type Lang = "en" | "ru";

const STORAGE_KEY = "pa-lang";

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Translate a Russian source string into the active language. */
  t: (ru: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const readInitialLang = (): Lang => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "ru" || stored === "en" ? stored : "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(readInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);

  const t = useCallback(
    (ru: string) => {
      if (lang === "ru") return ru;
      return dictionary[ru] ?? ru;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return { lang: "en", setLang: () => {}, t: (ru: string) => dictionary[ru] ?? ru };
  }
  return ctx;
};

/** Convenience hook: returns only the translate function. */
export const useT = () => useLanguage().t;
